import { reactive } from "vue";
import type { OutreachCueCardsData, OutreachHistoryItem } from "@/types/outreach-cue-cards";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import router from "@/router";

const $toast = useToast();

interface CueCardsCacheEntry {
  data: OutreachCueCardsData;
  recordId: string;
  icpId: string;
  isSaved: boolean;
}

const cache: Record<string, CueCardsCacheEntry> = reactive({});

export function setCueCards(
  contactId: string,
  data: OutreachCueCardsData,
  recordId: string,
  icpId: string,
  isSaved = false
) {
  cache[contactId] = { data, recordId, icpId, isSaved };
}

export function getCueCards(contactId: string): CueCardsCacheEntry | null {
  return cache[contactId] || null;
}

export function updateCueCardsSaved(contactId: string, isSaved: boolean) {
  if (cache[contactId]) {
    cache[contactId].isSaved = isSaved;
  }
}

export function clearCueCards(contactId: string) {
  delete cache[contactId];
}

// --- History list TTL cache (5-min per contact) ---

interface HistoryCacheEntry {
  items: OutreachHistoryItem[];
  fetchedAt: number;
}

const historyCache: Record<string, HistoryCacheEntry> = reactive({});
const HISTORY_TTL_MS = 5 * 60 * 1000;

export function getHistoryCache(contactId: string): OutreachHistoryItem[] | null {
  const e = historyCache[contactId];
  if (!e || Date.now() - e.fetchedAt > HISTORY_TTL_MS) return null;
  return e.items;
}

export function setHistoryCache(contactId: string, items: OutreachHistoryItem[]) {
  historyCache[contactId] = { items, fetchedAt: Date.now() };
}

export function invalidateHistoryCache(contactId: string) {
  delete historyCache[contactId];
}

// --- In-flight dedup for mount-time fetches ---

const inflightFetches: Record<string, Promise<OutreachHistoryItem[]>> = {};

export function fetchOutreachHistory(contactId: string): Promise<OutreachHistoryItem[]> {
  if (contactId in inflightFetches) {
    return inflightFetches[contactId];
  }
  const promise = AIGenerationService.getOutreachHistory(contactId)
    .then((items) => {
      setHistoryCache(contactId, items);
      return items;
    })
    .finally(() => {
      delete inflightFetches[contactId];
    });
  inflightFetches[contactId] = promise;
  return promise;
}

// --- Background generation system ---

type GenerationCallbacks = {
  onSuccess: () => void;
  onError: (err: Error) => void;
};

const mountedCallbacks: Record<string, GenerationCallbacks> = {};
const inflightGenerations: Record<string, Promise<void>> = {};

export function registerMountedComponent(contactId: string, callbacks: GenerationCallbacks) {
  mountedCallbacks[contactId] = callbacks;
}

export function unregisterMountedComponent(contactId: string) {
  delete mountedCallbacks[contactId];
}

export function isGenerationPending(contactId: string): boolean {
  return contactId in inflightGenerations;
}

export function startBackgroundGeneration(
  contactId: string,
  icpId: string,
  contactName: string,
  returnRouteName: string,
  returnQuery: Record<string, any>,
  force?: boolean
) {
  if (contactId in inflightGenerations) return;

  const promise = AIGenerationService.generateAllOutreach({ contactId, icpId, force })
    .then((response) => {
      const { id, ...cardData } = response;
      setCueCards(contactId, cardData, id, icpId);
      invalidateHistoryCache(contactId);

      const callbacks = mountedCallbacks[contactId];
      if (callbacks) {
        callbacks.onSuccess();
      } else {
        const displayName = contactName || "this contact";
        $toast.success(`Q cards ready for ${displayName}`, {
          duration: 10000,
          action: {
            label: "View",
            onClick: () => {
              router.push({ name: returnRouteName, query: returnQuery });
            },
          },
        });
      }
    })
    .catch((err: Error) => {
      const callbacks = mountedCallbacks[contactId];
      if (callbacks) {
        callbacks.onError(err);
      } else {
        $toast.error("Q card generation failed. Please try again.");
      }
    })
    .finally(() => {
      delete inflightGenerations[contactId];
    });

  inflightGenerations[contactId] = promise;
}
